import React, { useEffect, useState } from 'react'
import { useSelector } from "react-redux"
import moment from 'moment';
import ApiClient from "../ApiClient"
import { notify } from '../utils/ToastUtils';
import { selectUser } from 'src/stores/userSlice';
import Tooltip from '@mui/material/Tooltip';
import { TextField, Select, MenuItem } from '@mui/material';
import CentralizedTable from "../components/CentralizedTable";
import ActionConfirmModal from "../components/ActionConfirmModal";
import { Bell, Megaphone, Trash2, Send } from "lucide-react";
import { GridToolbar } from '@mui/x-data-grid';
import { EVENT_TYPES } from '../utils/constants';

const getTableCols = (setDeleteModal) => [{
    headerName: "Type",
    field: "TYPE",
    sortable: true,
    width: 120,
    renderCell: (params) => {
        const isAlert = params?.row?.TYPE === "Alert";
        return (
            <div className="flex items-center h-full">
                <div className={`px-2.5 py-1 rounded-md text-[11px] font-bold flex items-center gap-1.5 w-fit uppercase tracking-wider ${isAlert ? 'bg-red-50 text-red-600 border border-red-200' : 'bg-blue-50 text-blue-600 border border-blue-200'}`}>
                    {isAlert ? <Bell size={12} strokeWidth={2.5} /> : <Megaphone size={12} strokeWidth={2.5} />}
                    {params?.row?.TYPE}
                </div>
            </div>
        )
    }
}, {
    headerName: "Event Type",
    field: "EVENT_TYPE",
    sortable: true,
    width: 130,
    renderCell: (params) => {
        const eventDetails = params?.row?.EVENT_DETAILS ? (typeof params.row.EVENT_DETAILS === 'string' ? JSON.parse(params.row.EVENT_DETAILS) : params.row.EVENT_DETAILS) : {};
        return <span className="text-[13px] font-medium text-slate-700">{eventDetails?.event_type || '-'}</span>;
    }
}, {
    headerName: "Title",
    field: "TITLE",
    sortable: true,
    width: 160,
    renderCell: (params) => {
        const eventDetails = params?.row?.EVENT_DETAILS ? (typeof params.row.EVENT_DETAILS === 'string' ? JSON.parse(params.row.EVENT_DETAILS) : params.row.EVENT_DETAILS) : {};
        return <span className="text-[13px] font-medium text-slate-700">{eventDetails?.title || '-'}</span>;
    }
}, {
    headerName: "Event Date",
    field: "EVENT_DATE",
    sortable: true,
    width: 120,
    renderCell: (params) => {
        const eventDetails = params?.row?.EVENT_DETAILS ? (typeof params.row.EVENT_DETAILS === 'string' ? JSON.parse(params.row.EVENT_DETAILS) : params.row.EVENT_DETAILS) : {};
        return <span className="text-[13px] font-medium text-slate-700">{eventDetails?.event_date || '-'}</span>;
    }
}, {
    headerName: "Time",
    field: "EVENT_TIME",
    sortable: true,
    width: 150,
    renderCell: (params) => {
        const eventDetails = params?.row?.EVENT_DETAILS ? (typeof params.row.EVENT_DETAILS === 'string' ? JSON.parse(params.row.EVENT_DETAILS) : params.row.EVENT_DETAILS) : {};
        return (
            <div className="flex flex-col justify-center h-full py-2">
                <span className="text-[13px] font-semibold text-slate-700">{eventDetails?.start_time ? `${eventDetails?.start_time} - ${eventDetails?.end_time}` : '-'}</span>
            </div>
        );
    }
}, {
    headerName: "Raised Date",
    field: "RAISED_DATE",
    sortable: true,
    width: 140,
    renderCell: (params) => (
        <div className="flex flex-col justify-center h-full py-2">
            <span className="text-[13px] font-semibold text-slate-700">{moment(params?.row?.RAISED_DATE).format("DD MMM YYYY")}</span>
            <span className="text-[11px] font-medium text-slate-400">{moment(params?.row?.RAISED_DATE).format("hh:mm A")}</span>
        </div>
    )
}, {
    headerName: "Description",
    field: "DESCRIPTION",
    sortable: true,
    flex: 1,
    minWidth: 250,
    renderCell: (params) => (
        <div className="flex items-center h-full">
            <span className="text-[13px] text-slate-700 font-medium truncate pr-4" title={params?.row?.DESCRIPTION}>
                {params?.row?.DESCRIPTION}
            </span>
        </div>
    )
}, {
    headerName: "Raised By",
    field: "REQUEST_RAISED_BY",
    sortable: true,
    width: 150,
    renderCell: (params) => (
        <span className="text-[13px] font-medium text-slate-700">{params?.row?.RAISED_BY}</span>
    )
}, {
    headerName: "Action",
    field: "Action",
    sortable: false,
    width: 100,
    renderCell: (params) => {
        return (
            <div className="flex items-center h-full">
                <Tooltip title="Delete" placement="top" arrow>
                    <span onClick={() => setDeleteModal({ isOpen: true, row: params?.row, loading: false })}>
                        <Trash2 
                            size={16} 
                            className="text-red-500 cursor-pointer hover:scale-110 transition-transform" 
                        />
                    </span>
                </Tooltip>
            </div>
        )
    }
}];

function HrNotifications() {
    const user = useSelector(selectUser)
    const initialState = {
        type: "",
        message: "",
        title: "",
        date: "",
        startTime: "",
        endTime: "",
        eventType: ""
    }
    const [state, setState] = useState(initialState)
    const [rows, setRows] = useState([])
    const [loading, setLoading] = useState({ fetch: true, submit: false });
    
    // Confirm Modal State
    const [deleteModal, setDeleteModal] = useState({ isOpen: false, row: null, loading: false });
    
    const columns = getTableCols(setDeleteModal);

    function handleChange(evt) {
        const { name, value } = evt.target;
        setState({
            ...state,
            [name]: value
        });
    }

    function handleChangeType(evt) {
        const value = evt.target.value;
        setState({
            ...state,
            type: value
        });
    }
    function handleChangeMessage(evt) {
        const value = evt.target.value;
        setState({
            ...state,
            message: value
        });
    }

    const isTimeValid = () => {
        if (state.type === 'Event' && state.startTime && state.endTime) {
            return state.startTime < state.endTime;
        }
        return true;
    };

    const isFormValid = () => {
        const wordCount = state.message.trim() ? state.message.trim().split(/\s+/).length : 0;
        if (!state.type) return false;
        if (wordCount === 0 || wordCount > 30) return false;
        
        if (state.type === 'Event') {
            if (!state.eventType || !state.title || !state.date || !state.startTime || !state.endTime) return false;
            if (!isTimeValid()) return false;
        }
        return true;
    };

    const createNotifications = async () => {
        const wordCount = state.message.trim() ? state.message.trim().split(/\s+/).length : 0;
        
        if (isFormValid()) {
            if (wordCount > 30) {
                notify.error("Message should not exceed 30 words");
                return;
            }
            
            const eventDetails = {
                title: state.title,
                event_date: state.date,
                start_time: state.startTime,
                end_time: state.endTime,
                event_type: state.eventType
            };

            const data = {
                'raised_by': user.name,
                'raised_date': moment(new Date()).format("YYYY-MM-DD HH:mm:ss"),
                'description': state.message,
                'type': state.type,
                'event_details': state.type === 'Event' ? JSON.stringify(eventDetails) : null
            }
            setLoading(prev => ({ ...prev, submit: true }));
            try {
                await ApiClient.post(`${process.env.REACT_APP_BASE_URL}/admin/addNotifications`, data)
                notify.success(`${state.type} added successfully`)
                setState(initialState)
                getAlertAndEvent();
            } catch (error) {
                console.log(error)
                notify.error("Something went wrong, please try again later")
            } finally {
                setLoading(prev => ({ ...prev, submit: false }));
            }
        } else {
            notify.error("All fields are required")
        }
    }

    const handleDelete = async () => {
        if (!deleteModal.row) return;
        setDeleteModal(prev => ({ ...prev, loading: true }));
        try {
            await ApiClient.delete(`${process.env.REACT_APP_BASE_URL}/admin/deleteAlertOrEvent`, { data:  { REQUEST_ID: deleteModal.row.REQUEST_ID }  })
            notify.success(`${deleteModal.row?.TYPE} Deleted Successfully`);
            getAlertAndEvent();
            setDeleteModal({ isOpen: false, row: null, loading: false });
        } catch (error) {
            notify.error("Something went wrong. Please try again later")
            setDeleteModal({ isOpen: false, row: null, loading: false });
        }
    }
    

    const getAlertAndEvent = async () => {
        try {
            setLoading(prev => ({ ...prev, fetch: true }));
            const { data } = await ApiClient.get(`${process.env.REACT_APP_BASE_URL}/dashboard/getAlertsAndEvents`)
            setRows(data)
        } catch (error) {
            console.log(error)
        } finally {
            setLoading(prev => ({ ...prev, fetch: false }));
        }
    }


    useEffect(() => {
        getAlertAndEvent();
    }, [])

    const wordCount = state.message.trim() ? state.message.trim().split(/\s+/).length : 0;

    return (
        <div className="d-flex profile flex-1 flex flex-col" style={{ fontFamily: '"Inter", sans-serif' }}>
            
            <ActionConfirmModal 
                isOpen={deleteModal.isOpen}
                onClose={() => !deleteModal.loading && setDeleteModal({ isOpen: false, row: null, loading: false })}
                onConfirm={handleDelete}
                title={`Delete ${deleteModal.row?.TYPE || 'Record'}`}
                message={`Are you sure you want to delete this ${deleteModal.row?.TYPE?.toLowerCase() || 'record'}? This action cannot be undone.`}
                loading={deleteModal.loading}
                type="danger"
                confirmText="Delete"
            />

            <div style={{ flex: "1 1 auto", display: "flex", flexFlow: "column" }}>
                <div style={{ flex: 1 }}>

                    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4 mb-3 max-w-[1700px] mx-auto w-full flex flex-col relative overflow-hidden group">
                        <div className="flex justify-between items-center mb-4">
                            <div className="flex items-center gap-3">
                                <div className="p-3 rounded-xl bg-blue-50 text-blue-600">
                                    <Megaphone size={20} strokeWidth={2} />
                                </div>
                                <div>
                                    <h3 className="text-[16px] font-bold text-slate-800 m-0 leading-tight mb-1">Add Events/Alerts for Employees</h3>
                                    <p className="text-xs text-slate-500 m-0">Create new events or alerts to broadcast to employees.</p>
                                </div>
                            </div>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            <div className="col-span-1">
                                <label className="block text-sm font-semibold text-slate-700 mb-2">Select Type <span className="text-red-500">*</span></label>
                                <Select 
                                    className="w-full bg-white" 
                                    size="small"
                                    name="type" 
                                    value={state.type} 
                                    required 
                                    onChange={handleChangeType}
                                    displayEmpty
                                    sx={{ height: '40px', fontSize: '14px' }}
                                    MenuProps={{
                                        sx: {
                                            '& .MuiMenuItem-root': {
                                                fontSize: '14px',
                                                minHeight: '36px'
                                            }
                                        }
                                    }}
                                >
                                    <MenuItem value='' disabled hidden>Select type...</MenuItem>
                                    <MenuItem value='Event'>Event</MenuItem>
                                    <MenuItem value='Alert'>Alert</MenuItem>
                                </Select>
                            </div>
                            
                            {state.type === 'Event' && (
                                <>
                                    <div className="col-span-1">
                                        <label className="block text-sm font-semibold text-slate-700 mb-2">Event Type <span className="text-red-500">*</span></label>
                                        <Select 
                                            className="w-full bg-white" 
                                            size="small"
                                            name="eventType" 
                                            value={state.eventType} 
                                            onChange={handleChange}
                                            displayEmpty
                                            sx={{ height: '40px', fontSize: '14px' }}
                                            MenuProps={{
                                                sx: {
                                                    '& .MuiMenuItem-root': {
                                                        fontSize: '14px',
                                                        minHeight: '36px'
                                                    }
                                                }
                                            }}
                                        >
                                            <MenuItem value='' disabled hidden>Select event type...</MenuItem>
                                            {EVENT_TYPES.map(event => (
                                                <MenuItem key={event.value} value={event.value}>{event.label}</MenuItem>
                                            ))}
                                        </Select>
                                    </div>
                                    <div className="col-span-1">
                                        <label className="block text-sm font-semibold text-slate-700 mb-2">Date <span className="text-red-500">*</span></label>
                                        <TextField 
                                            type="date"
                                            className="w-full bg-white" 
                                            size="small"
                                            name="date"
                                            value={state.date} 
                                            onChange={handleChange} 
                                            InputLabelProps={{ shrink: true }}
                                            sx={{ '& .MuiInputBase-root': { height: '40px', fontSize: '14px' } }}
                                        />
                                    </div>
                                    <div className="col-span-1">
                                        <label className="block text-sm font-semibold text-slate-700 mb-2">Title <span className="text-red-500">*</span></label>
                                        <TextField 
                                            type="text"
                                            className="w-full bg-white" 
                                            size="small"
                                            placeholder="Event title" 
                                            name="title"
                                            value={state.title} 
                                            onChange={handleChange} 
                                            sx={{ '& .MuiInputBase-root': { height: '40px', fontSize: '14px' } }}
                                        />
                                    </div>
                                    <div className="col-span-1">
                                        <label className="block text-sm font-semibold text-slate-700 mb-2">Start Time <span className="text-red-500">*</span></label>
                                        <TextField 
                                            type="time"
                                            className="w-full bg-white" 
                                            size="small"
                                            name="startTime"
                                            value={state.startTime} 
                                            onChange={handleChange} 
                                            InputLabelProps={{ shrink: true }}
                                            sx={{ '& .MuiInputBase-root': { height: '40px', fontSize: '14px' } }}
                                        />
                                    </div>
                                    <div className="col-span-1">
                                        <label className="block text-sm font-semibold text-slate-700 mb-2">End Time <span className="text-red-500">*</span></label>
                                        <TextField 
                                            type="time"
                                            className="w-full bg-white" 
                                            size="small"
                                            name="endTime"
                                            value={state.endTime} 
                                            onChange={handleChange} 
                                            InputLabelProps={{ shrink: true }}
                                            sx={{ 
                                                '& .MuiInputBase-root': { height: '40px', fontSize: '14px' },
                                                '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: '#ef4444 !important' }
                                            }}
                                            error={state.type === 'Event' && !!state.startTime && !!state.endTime && state.startTime >= state.endTime}
                                            helperText={(state.type === 'Event' && state.startTime && state.endTime && state.startTime >= state.endTime) ? "Must be after start time" : ""}
                                        />
                                    </div>
                                </>
                            )}

                            <div className="col-span-1 md:col-span-4">
                                <label className="block text-sm font-semibold text-slate-700 mb-2">Message <span className="text-red-500">*</span></label>
                                <textarea 
                                    rows={3} 
                                    className="w-full bg-white border border-slate-200 rounded-xl p-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors resize-none" 
                                    placeholder="Enter message to broadcast..." 
                                    name="message"
                                    value={state.message} 
                                    onChange={handleChangeMessage} 
                                    required
                                ></textarea>
                                
                                <div className="flex flex-col md:flex-row items-start md:items-center justify-between mt-3 gap-4">
                                    <div className={`flex items-center gap-2 text-sm font-medium ${wordCount > 30 ? 'text-red-500' : 'text-slate-500'}`}>
                                        {wordCount} / 30 words
                                    </div>
                                    <button 
                                        className="px-6 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors text-sm font-semibold shadow-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed" 
                                        onClick={createNotifications} 
                                        disabled={loading.submit || !isFormValid()}
                                    >
                                        {loading.submit ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Send size={16} />}
                                        Submit
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden mb-4 max-w-[1700px] mx-auto w-full">
                        <div className="p-0 pt-1">
                            <CentralizedTable
                                isLoading={loading.fetch}
                                renderTopToolbarCustomActions={() => (
                                    <div className="flex items-center gap-3 py-2">
                                        <div className="p-3 rounded-xl bg-green-50 text-green-600">
                                            <Bell size={20} strokeWidth={2} />
                                        </div>
                                        <div>
                                            <h2 className="text-[16px] font-bold text-slate-800 m-0 leading-tight">Upcoming Events/Alerts</h2>
                                            <p className="text-xs text-slate-500 font-medium mt-0.5 block m-0">Track and manage active broadcasts.</p>
                                        </div>
                                    </div>
                                )}
                                className="[&_.MuiTableCell-root]:font-['Montserrat'] [&_.MuiTableCell-root]:!py-1.5 [&_.MuiDataGrid-cell]:!py-1.5 [&_.MuiTablePagination-selectLabel]:font-['Montserrat'] [&_.MuiTablePagination-select]:font-['Montserrat'] [&_.MuiTablePagination-displayedRows]:font-['Montserrat'] [&_.MuiDataGrid-columnHeaderTitle]:font-['Montserrat'] [&_.MuiDataGrid-columnHeaderTitle]:font-bold"
                                getRowId={rows => rows.REQUEST_ID}
                                rows={rows ?? []}
                                columns={columns}
                                rowHeight={38}
                                columnHeaderHeight={42}
                                muiTableBodyCellProps={{ sx: { paddingTop: loading.fetch ? '8px' : '0px', paddingBottom: loading.fetch ? '8px' : '0px' } }}
                                autoHeight
                                initialState={{
                                    pagination: {
                                        paginationModel: { pageSize: 10, page: 0 },
                                    },
                                }}
                                slots={{
                                    toolbar: GridToolbar,
                                }}
                                pageSizeOptions={[10, 25, 50]}
                                hideFooterSelectedRowCount
                                muiTableContainerProps={{
                                    sx: {
                                        minHeight: "auto",
                                        height: 'auto',
                                        flex: 1,
                                        overflow: 'auto',
                                    },
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default HrNotifications